import React, { useState, useEffect, useRef } from 'react';
import './Projects.css';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const projects = [
    {
      id: 1,
      title: 'Studyvarious',
      description: 'Violin learning website that offers multiple compositions, displays real-time TAB and notes.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe'],
      category: 'web',
      link: 'https://studyvarious.com',
      image: '🎻',
    },
    {
      id: 2,
      title: 'Wordle RL Agent',
      description: 'RL agent that learns to play Wordle (pre-chatGPT).',
      technologies: ['Python', 'Reinforcement Learning', 'PyTorch', 'NumPy'],
      category: 'ai',
      link: 'https://github.com/noamjanco/wordle_rl',
      image: '🎯'
    },
    {
      id: 4,
      title: 'Fake Chwazi',
      description: 'Biased Chwazi that is using AI/ML to make you win! (iOS)',
      technologies: ['SwiftUI'],
      category: 'mobile',
      link: '#',
      image: '☝️',
    },
    {
      id: 5,
      title: 'Unrolling Algorithms for Group Synchronization',
      description: 'This is the original implementation of the experiments conducted in "Unrolled algorithms for group synchronization" (https://arxiv.org/abs/2207.09418).',
      technologies: ['Python', 'Deep Learning', 'Signal Processing'],
      category: 'research',
      link: 'https://github.com/noamjanco/unrolling_synchronization',
      image: '🧬',
    },
    {
      id: 6,
      title: 'Synch-EM',
      description: 'This is the original implementation of the experiments conducted in "An Accelerated Expectation-Maximization Algorithm for Multi-Reference Alignment" (https://ieeexplore.ieee.org/document/9797053).',
      technologies: ['Python', 'Statistical Machine learning', 'Signal Processing'],
      category: 'research',
      link: 'https://github.com/noamjanco/synch-em',
      image: '🧬',
    },
    {
      id: 7,
      title: 'Cooperation Among Robots',
      description: 'Deployment of Ad-Hoc WiFi network by cooperating multi-robot system based on Android devices. B.Sc. final project.',
      posterLink: '/robots_poster.jpg',
      technologies: ['Hardware', 'Arduino', 'Android', 'Java', 'Matlab'],
      category: 'robotics',
      link: 'https://www.youtube.com/watch?v=CtAavhF_WnI',
      image: '🤖',
    },
    {
      id: 8,
      title: 'Roadcast.ai',
      description: 'Generative AI-based platform for personalized podcasts based on user interests and location',
      technologies: ['LLM', 'React Native', 'Python', 'Flask', 'TTS', 'Stable-diffusion', 'Groq-cloud'],
      category: 'ai',
      link: '#',
      image: '🎙️',
    },
  ];

  const filters = ['all', 'web', 'mobile', 'ai', 'research', 'robotics'];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="projects" className="projects" ref={sectionRef}>
      <div className="container">
        <div className={`projects-header ${isVisible ? 'visible' : ''}`}>
          <h2 className="section-title">Featured Projects</h2>
          <div className="title-underline"></div>
          <p className="section-subtitle">Showcasing my latest work and achievements</p>
        </div>

        <div className={`project-filters ${isVisible ? 'visible' : ''}`}>
          {filters.map(filter => (
            <button
              key={filter}
              className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter)}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>

        <div className={`projects-grid ${isVisible ? 'visible' : ''}`}>
          {filteredProjects.map((project, index) => (
            <div 
              key={project.id} 
              className="project-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="project-image">
                <span className="project-emoji">{project.image}</span>
                <div className="project-overlay">
                  <a href={project.link} className="project-link">View Project →</a>
                </div>
              </div>
              <div className="project-content">
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">
                  {project.description}
                  {project.posterLink && (
                    <> <a href={project.posterLink} target="_blank" rel="noopener noreferrer" className="poster-link">[View Poster]</a></>
                  )}
                </p>
                <div className="project-technologies">
                  {project.technologies.map((tech, i) => (
                    <span key={i} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
